import { useCallback } from 'react';

import type { Action, ActionId } from '@atlassian/edition-awareness';

import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

function useGetAutomationAction() {
	return useCallback(async (): Promise<Action<'automation'>> => {
		return {
			id: 'automation',
			type: 'link',
			href: `/wiki/admin/automation#/tab/rule-library`,
		};
	}, []);
}

function usePagePermissionsAction(showPagePermissionsSpotlight: () => void) {
	const spaceKey = useSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();

	return useCallback(async (): Promise<Action<'page-permissions'>> => {
		if (!contentId || !spaceKey || !contentType) {
			return {
				id: 'page-permissions',
				type: 'link',
				href: `/wiki/create-content/page?journeyId=onboarding-page-permissions`,
			};
		}

		return {
			id: 'page-permissions',
			type: 'fn',
			fn: showPagePermissionsSpotlight,
		};
	}, [contentId, contentType, spaceKey, showPagePermissionsSpotlight]);
}

function useGetGuestAccessAction() {
	return useCallback(async (): Promise<Action<'guest-access'>> => {
		return {
			id: 'guest-access',
			type: 'link',
			href: '/wiki/admin/permissions/global?tab=external',
		};
	}, []);
}

function useGetWhiteboardsAction() {
	const spaceKey = useSpaceKey();

	return useCallback(async (): Promise<Action<'whiteboards'>> => {
		const href = buildCreationLink({
			contentType: 'whiteboard',
			source: 'editionAwarenessActions',
			spaceKey: spaceKey ?? undefined,
			openTemplatePicker: true,
		});

		return {
			id: 'whiteboards',
			type: 'link',
			href,
		};
	}, [spaceKey]);
}

function useGetMissionControlAction() {
	const { edition } = useSessionData();

	return useCallback(async (): Promise<Action<'mission-control'> | null> => {
		if (edition !== ConfluenceEdition.PREMIUM) {
			// Mission control is a premium only feature
			return null;
		}

		return {
			id: 'mission-control',
			type: 'link',
			href: '/wiki/admin/center',
		};
	}, [edition]);
}

export function useActions({
	showPagePermissionsSpotlight,
}: {
	showPagePermissionsSpotlight: () => void;
}) {
	const getAutomationAction = useGetAutomationAction();
	const getGuestAccessAction = useGetGuestAccessAction();
	const getWhiteboardsAction = useGetWhiteboardsAction();
	const getPagePermissionsAction = usePagePermissionsAction(showPagePermissionsSpotlight);
	const getMissionControlAction = useGetMissionControlAction();

	return useCallback(
		async (actionId: ActionId): Promise<Action<ActionId> | null> => {
			switch (actionId) {
				case 'automation':
					return getAutomationAction();
				case 'guest-access':
					return getGuestAccessAction();
				case 'page-permissions':
					return getPagePermissionsAction();
				case 'whiteboards':
					return getWhiteboardsAction();
				case 'mission-control':
					return getMissionControlAction();
				default:
					return null;
			}
		},
		[
			getAutomationAction,
			getGuestAccessAction,
			getMissionControlAction,
			getPagePermissionsAction,
			getWhiteboardsAction,
		],
	);
}
